/**
 * "Call the next one now" for one campaign, from the automation tab.
 *
 * The same request and the same note as the campaign editor's button
 * (`lib/aiCampaignCallNext.ts`, worded with that card's keys), so both places
 * say the same thing about the same answer. The server works the stored
 * campaign through every gate except the automation's switch, the gap and the
 * campaign's own switch (in test mode also the calling window): a real call
 * still waits for the window. Whatever happened, the tab re-reads its status,
 * since a press may have placed a call or taken companies out of the queue.
 */
import { useState } from "react";
import { Loader2, PhoneOutgoing } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { callNextAiCampaign, callNextNote, type CallNextNote } from "../../../../lib/aiAutomation";
import { Btn, StatusPill } from "../../../ui";
import { Note } from "../../settings/panel/fields";

export function CallNextButton({
  campaignId,
  disabled,
  onCalled,
}: {
  campaignId: string;
  disabled?: boolean;
  onCalled: () => Promise<void>;
}) {
  const t = useT();
  const [calling, setCalling] = useState(false);
  const [note, setNote] = useState<CallNextNote | null>(null);

  async function press() {
    if (calling) return;
    setCalling(true);
    setNote(null);
    try {
      setNote(callNextNote({ reply: await callNextAiCampaign(campaignId) }));
    } catch (error) {
      setNote(callNextNote({ error }));
    }
    await onCalled();
    setCalling(false);
  }

  function text(shown: CallNextNote): string {
    if (shown.kind === "placed") {
      return shown.companyName
        ? t("aiCalls.campaigns.automation.callNext.placed", { company: shown.companyName })
        : t("aiCalls.campaigns.automation.callNext.placedNoName");
    }
    if (shown.kind === "error") return t(`aiCalls.campaigns.automation.callNext.error.${shown.code}`);
    // A reason newer than this build's catalogue comes back as the key itself.
    const worded = t(shown.key);
    return worded === shown.key ? t("aiCalls.campaigns.automation.callNext.unknown", { code: shown.code }) : worded;
  }

  return (
    <>
      <Btn small onClick={() => void press()} disabled={disabled || calling}>
        {calling ? <Loader2 className="size-3.5 animate-spin" /> : <PhoneOutgoing className="size-3.5" />}
        {t("aiCalls.automation.campaigns.callNext")}
      </Btn>
      {note ? (
        <Note tone={note.tone} live className="basis-full">
          <span className="flex flex-wrap items-center gap-2">
            {text(note)}
            {note.kind !== "error" && note.test ? <StatusPill tone="info">{t("aiCalls.test")}</StatusPill> : null}
          </span>
        </Note>
      ) : null}
    </>
  );
}
